import { useEffect, useSyncExternalStore } from "react";
import type { AssistantEntity } from "./types";

// Module-level registry of the record the user is currently looking at.
// Detail pages register once their data loads and clear on unmount.

let current: AssistantEntity | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export function setAssistantContext(entity: AssistantEntity | null): void {
  if (
    current &&
    entity &&
    current.type === entity.type &&
    current.id === entity.id &&
    current.name === entity.name
  ) {
    return;
  }
  current = entity;
  emit();
}

export function clearAssistantContext(): void {
  if (current === null) return;
  current = null;
  emit();
}

export function getAssistantContext(): AssistantEntity | null {
  return current;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Read the registered entity (re-renders on change). */
export function useRegisteredAssistantContext(): AssistantEntity | null {
  return useSyncExternalStore(subscribe, getAssistantContext, getAssistantContext);
}

/**
 * Register an entity for the lifetime of the calling component.
 * Pass null while data is loading. Clears on unmount.
 */
export function useAssistantContext(entity: AssistantEntity | null): void {
  const type = entity?.type;
  const id = entity?.id;
  const name = entity?.name;
  useEffect(() => {
    if (type && id) setAssistantContext({ type, id, name });
  }, [type, id, name]);
  useEffect(() => {
    if (!type || !id) return;
    return () => {
      const c = getAssistantContext();
      if (c && c.type === type && c.id === id) clearAssistantContext();
    };
  }, [type, id]);
}
